import React, { FC, useMemo } from "react";
import i18n from "@/i18n";
import { Table } from "../components/inline/Table";
import { ScreenProps } from "../typings";
import { TableView as ITableView } from "@data-root/types";
import {
  Inline,
  UseRowsContext,
  useAppContext,
  useAsyncCallback,
  useClientContext,
  useNavigation,
  useRows,
  useViewFilters,
  ViewFooter,
  ViewHeader,
  useGroups,
  UseGroupsContext,
  useViewContext,
} from "@data-root/react";
import { Layout, Typography, message } from "antd";
import FloatButton from "../components/FloatButton";
import { PlusOutlined } from "@ant-design/icons";
import { v4 } from "uuid";
import { ErrorView } from "@data-root/error-boundary";

/**
 * #343: a demo embed minted in the READ-ONLY ('viewer'/'prospect') role can read but never write
 * (the demo-driver's assertWritable 403s with "This demo is read-only for your role"). Detect that
 * role from core.user.permissions (userFromMetadata maps the minted role into permissions) so we can
 * (a) HIDE the Add affordance and (b) treat a read-only write rejection as a calm, non-fatal toast
 * instead of crashing the whole view. 'owner'/'salesperson'/'super_admin' are writable; absence of a
 * demo role (a normal logged-in app) is writable too.
 */
const READ_ONLY_ROLES = ["viewer", "prospect"];
function isReadOnlyUser(user: { permissions?: string[] } | null | undefined): boolean {
  const perms = user?.permissions ?? [];
  if (perms.length === 0) return false; // not a demo session -> normal writable app
  if (perms.some((p) => p === "owner" || p === "salesperson" || p === "super_admin")) return false;
  return perms.some((p) => READ_ONLY_ROLES.includes(p));
}

/** True when an error is the demo-driver's read-only write rejection (non-fatal for the view). */
function isReadOnlyError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return /read-only for your role/i.test(msg);
}

const { Content } = Layout;
const { Text } = Typography;

export const TableView: FC<ScreenProps<ITableView>> = ({ view }) => {
  const { tableId, options, noAdd } = view;

  const { core } = useClientContext();
  const { appSpecifier, styles, getTable } = useAppContext();
  const { navigation } = useNavigation();

  const table = useMemo(() => getTable(tableId), [getTable, tableId]);

  const { filters: contextFilters, sort: contextSort } = useViewContext();
  const { filters: viewFilters, sort: viewSort } = useViewFilters(view);

  const groupsContext = useGroups(core, appSpecifier, tableId);
  const rowsContext = useRows(
    core,
    appSpecifier,
    tableId,
    [...contextFilters, ...viewFilters, ...groupsContext.filters],
    [...contextSort, ...viewSort]
  );

  const {
    getContext,
    displayRows,
    loaded,
    fetch,
    create,
    _delete,
    totalRows,
    fetching,
    error,
  } = rowsContext;

  // #343: read-only demo viewers can't write. Hide Add + swallow the read-only rejection calmly.
  const readOnly = useMemo(() => isReadOnlyUser(core.user), [core.user]);

  const onNew = useAsyncCallback(async () => {
    try {
      const context = getContext();
      const { rowId } = await create({});

      const onNew = await core.evaluateFormula<string>(options.onNew, context);
      if (onNew) navigation.navigate(onNew, { rowId });
    } catch (err) {
      // A read-only demo write 403s -> show a calm notice, never crash the view.
      if (isReadOnlyError(err)) {
        message.info(i18n.t("read_only_demo_notice"));
        return;
      }
      throw err;
    }
  }, [core, create, getContext, navigation, options.onNew]);

  const containerId = useMemo(() => v4(), []);

  // A read-only rejection is NOT a fatal view error - render the table (read-only) instead of the
  // full-screen ErrorView. Any other error still surfaces as before.
  if (error && !isReadOnlyError(error)) return <ErrorView error={error} />;

  return (
    <UseRowsContext.Provider value={rowsContext}>
      <UseGroupsContext.Provider value={groupsContext}>
        <Inline table={table} view={view} onDelete={(row) => _delete(row.id)}>
          <Layout>
            <ViewHeader view={view} getContext={getContext} />

            <Content id={containerId} style={{ overflow: "auto", padding: 10 }}>
              <Table
                containerId={containerId}
                getContext={getContext}
                loaded={loaded}
                totalRows={totalRows}
                rows={displayRows}
                fetching={fetching}
                onFetch={fetch}
                fields={view.options.columns}
              />
            </Content>

            <ViewFooter>
              <Text strong>{totalRows} {i18n.t("records")}</Text>
            </ViewFooter>

            {!noAdd && !readOnly && (
              <FloatButton
                shape="square"
                onClick={onNew}
                icon={<PlusOutlined style={{ color: styles.darkTintColor }} />}
              />
            )}
          </Layout>
        </Inline>
      </UseGroupsContext.Provider>
    </UseRowsContext.Provider>
  );
};
